import { useState, useEffect, useCallback, useRef } from 'react';
import { FaMousePointer } from 'react-icons/fa';
import styles from './StepByStepGuide.module.css';

export interface GuideStep {
  target: string;
  title: string;
  description: string;
  action: 'click' | 'observe';
  shield?: boolean;
}

interface StepByStepGuideProps {
  steps: GuideStep[];
  onComplete: () => void;
  onDismiss: () => void;
}

interface Position {
  top: number;
  left: number;
  width: number;
  height: number;
}

type TooltipPlacement = 'bottom' | 'top' | 'right';

function getTooltipPlacement(targetRect: DOMRect): TooltipPlacement {
  const spaceBelow = window.innerHeight - targetRect.bottom;
  const spaceAbove = targetRect.top;
  const spaceRight = window.innerWidth - targetRect.right;

  if (spaceBelow >= 180) return 'bottom';
  if (spaceAbove >= 180) return 'top';
  if (spaceRight >= 360) return 'right';
  return 'bottom';
}

function getTooltipStyle(targetRect: DOMRect, placement: TooltipPlacement): React.CSSProperties {
  const padding = 12;

  switch (placement) {
    case 'bottom':
      return {
        top: targetRect.bottom + padding,
        left: Math.max(8, Math.min(targetRect.left, window.innerWidth - 350)),
      };
    case 'top':
      return {
        bottom: window.innerHeight - targetRect.top + padding,
        left: Math.max(8, Math.min(targetRect.left, window.innerWidth - 350)),
      };
    case 'right':
      return {
        top: targetRect.top,
        left: targetRect.right + padding,
      };
  }
}

function getArrowClassName(placement: TooltipPlacement): string {
  switch (placement) {
    case 'bottom': return styles.tooltipArrowBottom;
    case 'top': return styles.tooltipArrowTop;
    case 'right': return styles.tooltipArrowLeft;
  }
}

// Tempo máximo (ms) para aguardar o target aparecer no DOM antes de pular
const TARGET_WAIT_TIMEOUT = 5000;
// Delay (ms) antes de exibir "Aguardando elemento carregar..." para evitar flash em transições rápidas
const WAITING_DISPLAY_DELAY = 1000;

export function StepByStepGuide({ steps, onComplete, onDismiss }: StepByStepGuideProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [targetPos, setTargetPos] = useState<Position | null>(null);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [waitingForTarget, setWaitingForTarget] = useState(false);
  const clickListenerRef = useRef<(() => void) | null>(null);
  const observeListenerRef = useRef<(() => void) | null>(null);
  const intervalRef = useRef<number | null>(null);
  const waitTimeoutRef = useRef<number | null>(null);
  const waitingDisplayTimeoutRef = useRef<number | null>(null);
  const targetFoundRef = useRef(false);

  const step = steps[currentStep];

  const advance = useCallback(() => {
    // Limpar timeouts pendentes
    if (targetLostTimeoutRef.current) {
      clearTimeout(targetLostTimeoutRef.current);
      targetLostTimeoutRef.current = null;
    }
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
      setTargetPos(null);
      setTargetRect(null);
      setWaitingForTarget(false);
      targetFoundRef.current = false;
    } else {
      onComplete();
    }
  }, [currentStep, steps.length, onComplete]);

  const targetLostTimeoutRef = useRef<number | null>(null);

  const updatePosition = useCallback(() => {
    if (!step) return;
    const el = document.querySelector(step.target);
    if (el) {
      targetFoundRef.current = true;
      setWaitingForTarget(false);
      // Cancelar timeout de "target perdido"
      if (targetLostTimeoutRef.current) {
        clearTimeout(targetLostTimeoutRef.current);
        targetLostTimeoutRef.current = null;
      }
      // Cancelar timeout de exibição do "Aguardando"
      if (waitingDisplayTimeoutRef.current) {
        clearTimeout(waitingDisplayTimeoutRef.current);
        waitingDisplayTimeoutRef.current = null;
      }
      const rect = el.getBoundingClientRect();
      const pad = 4;
      setTargetPos({
        top: rect.top - pad,
        left: rect.left - pad,
        width: rect.width + pad * 2,
        height: rect.height + pad * 2,
      });
      setTargetRect(rect);
    } else {
      setTargetPos(null);
      setTargetRect(null);
      // Se o target já existia e desapareceu (ex: modal fechou), avança rápido
      if (targetFoundRef.current && !targetLostTimeoutRef.current) {
        targetLostTimeoutRef.current = window.setTimeout(() => {
          targetLostTimeoutRef.current = null;
          advance();
        }, 600);
      }
    }
  }, [step, advance]);

  // Polling para detectar target no DOM + atualizar posicao
  useEffect(() => {
    targetFoundRef.current = false;
    updatePosition();
    intervalRef.current = window.setInterval(updatePosition, 200);

    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (targetLostTimeoutRef.current) clearTimeout(targetLostTimeoutRef.current);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [updatePosition]);

  // Timeout: se o target não aparece no DOM em X segundos, pula para o próximo step
  useEffect(() => {
    if (waitTimeoutRef.current) {
      clearTimeout(waitTimeoutRef.current);
      waitTimeoutRef.current = null;
    }
    if (waitingDisplayTimeoutRef.current) {
      clearTimeout(waitingDisplayTimeoutRef.current);
      waitingDisplayTimeoutRef.current = null;
    }

    // Se o target já foi encontrado, não precisa de timeout
    if (targetFoundRef.current) return;

    // Delay antes de mostrar "Aguardando" para evitar flash em transições rápidas
    waitingDisplayTimeoutRef.current = window.setTimeout(() => {
      if (!targetFoundRef.current) {
        setWaitingForTarget(true);
      }
    }, WAITING_DISPLAY_DELAY);

    waitTimeoutRef.current = window.setTimeout(() => {
      if (!targetFoundRef.current) {
        // Target não encontrado, pular step
        advance();
      }
    }, TARGET_WAIT_TIMEOUT);

    return () => {
      if (waitTimeoutRef.current) {
        clearTimeout(waitTimeoutRef.current);
        waitTimeoutRef.current = null;
      }
      if (waitingDisplayTimeoutRef.current) {
        clearTimeout(waitingDisplayTimeoutRef.current);
        waitingDisplayTimeoutRef.current = null;
      }
    };
  }, [currentStep, advance]);

  // Para passos 'click': escutar clique no target e avancar
  useEffect(() => {
    if (clickListenerRef.current) {
      clickListenerRef.current();
      clickListenerRef.current = null;
    }

    if (!step || step.action !== 'click') return;

    const attachInterval = window.setInterval(() => {
      const el = document.querySelector(step.target);
      if (el) {
        const handleClick = () => advance();
        el.addEventListener('click', handleClick, { once: true });
        clearInterval(attachInterval);
        clickListenerRef.current = () => {
          el.removeEventListener('click', handleClick);
        };
      }
    }, 200);

    return () => {
      clearInterval(attachInterval);
      if (clickListenerRef.current) {
        clickListenerRef.current();
        clickListenerRef.current = null;
      }
    };
  }, [currentStep, step, advance]);

  // Auto-avanço para passos 'observe' em campos de formulário
  useEffect(() => {
    if (observeListenerRef.current) {
      observeListenerRef.current();
      observeListenerRef.current = null;
    }

    if (!step || step.action !== 'observe') return;

    // Só auto-avançar em campos de formulário (campo-*)
    const isCampo = step.target.includes('campo-');
    if (!isCampo) return;

    const attachInterval = window.setInterval(() => {
      const el = document.querySelector(step.target);
      if (!el) return;

      clearInterval(attachInterval);

      // Pular auto-avanço para checkboxes/toggles
      const hasTextInput = el.querySelector(
        'input:not([type="checkbox"]):not([type="radio"]):not([type="hidden"]):not([type="button"]), textarea, select'
      );
      if (!hasTextInput) return;

      let hasInteracted = false;
      let advanceTimeout: number | null = null;

      const handleFocusIn = () => {
        hasInteracted = true;
        if (advanceTimeout) {
          clearTimeout(advanceTimeout);
          advanceTimeout = null;
        }
      };

      const handleFocusOut = (e: FocusEvent) => {
        if (!hasInteracted) return;

        const relatedTarget = e.relatedTarget as Node | null;
        if (relatedTarget && el.contains(relatedTarget)) return;

        advanceTimeout = window.setTimeout(() => {
          advance();
        }, 500);
      };

      el.addEventListener('focusin', handleFocusIn);
      el.addEventListener('focusout', handleFocusOut as EventListener);

      observeListenerRef.current = () => {
        el.removeEventListener('focusin', handleFocusIn);
        el.removeEventListener('focusout', handleFocusOut as EventListener);
        if (advanceTimeout) clearTimeout(advanceTimeout);
      };
    }, 200);

    return () => {
      clearInterval(attachInterval);
      if (observeListenerRef.current) {
        observeListenerRef.current();
        observeListenerRef.current = null;
      }
    };
  }, [currentStep, step, advance]);

  // Enquanto aguarda o target aparecer, mostra indicador de carregamento
  if (!step) return null;
  if (!targetPos || !targetRect) {
    if (waitingForTarget) {
      return (
        <div className={styles.overlay} style={{ pointerEvents: 'none' }}>
          <div className={styles.tooltip} style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)', pointerEvents: 'auto' }}>
            <div className={styles.tooltipHeader}>
              <span className={styles.stepCounter}>
                Passo {currentStep + 1} de {steps.length}
              </span>
              <button className={styles.btnSkip} onClick={onDismiss} title="Pular guia">
                &times;
              </button>
            </div>
            <div className={styles.tooltipTitle}>{step.title}</div>
            <div className={styles.tooltipDescription}>Aguardando elemento carregar...</div>
            <div className={styles.tooltipFooter}>
              <button className={styles.btnNext} onClick={advance}>
                Pular
              </button>
            </div>
          </div>
        </div>
      );
    }
    return null;
  }

  const placement = getTooltipPlacement(targetRect);
  const tooltipStyle = getTooltipStyle(targetRect, placement);
  const arrowClass = getArrowClassName(placement);

  // Clip-path que cobre tudo EXCETO a area do spotlight (cria um "buraco")
  const shieldClipPath = `polygon(
    0% 0%, 0% 100%,
    ${targetPos.left}px 100%, ${targetPos.left}px ${targetPos.top}px,
    ${targetPos.left + targetPos.width}px ${targetPos.top}px,
    ${targetPos.left + targetPos.width}px ${targetPos.top + targetPos.height}px,
    ${targetPos.left}px ${targetPos.top + targetPos.height}px, ${targetPos.left}px 100%,
    100% 100%, 100% 0%
  )`;

  // Shield ativado apenas em steps 'click' (por padrão), observe permite interação
  const showShield = step.shield !== undefined ? step.shield : step.action === 'click';

  return (
    <div className={styles.overlay} style={!showShield ? { pointerEvents: 'none' } : undefined}>
      {showShield && <>
        {/* Escudo de cliques - bloqueia cliques fora do spotlight */}
        <div
          className={styles.clickShield}
          style={{ clipPath: shieldClipPath }}
        />

        {/* Spotlight */}
        <div
          className={styles.spotlight}
          style={{
            top: targetPos.top,
            left: targetPos.left,
            width: targetPos.width,
            height: targetPos.height,
          }}
        />
      </>}

      {!showShield && (
        <div
          className={styles.spotlight}
          style={{
            top: targetPos.top,
            left: targetPos.left,
            width: targetPos.width,
            height: targetPos.height,
          }}
        />
      )}

      {/* Tooltip */}
      <div className={styles.tooltip} style={{ ...tooltipStyle, ...(!showShield ? { pointerEvents: 'auto' } : {}) }}>
        <div className={`${styles.tooltipArrow} ${arrowClass}`} />

        <div className={styles.tooltipHeader}>
          <span className={styles.stepCounter}>
            Passo {currentStep + 1} de {steps.length}
          </span>
          <button className={styles.btnSkip} onClick={onDismiss} title="Pular guia">
            &times;
          </button>
        </div>

        <div className={styles.tooltipTitle}>{step.title}</div>
        <div className={styles.tooltipDescription}>{step.description}</div>

        {step.action === 'click' ? (
          <div className={styles.clickHint}>
            <FaMousePointer className={styles.clickIcon} size={12} />
            Clique no elemento destacado
          </div>
        ) : (
          <div className={styles.tooltipFooter}>
            <button className={styles.btnNext} onClick={advance}>
              {currentStep < steps.length - 1 ? 'Próximo' : 'Concluir'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
